/** What the anonymous invitation page gets from `GET api/user-invitations/{id}` (trimmed, 2026-10-23). */
export interface PublicInvite {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  inviteAccepted?: boolean;
  /** Since 2026-10-25: the link works 14 days after the last email. */
  expired?: boolean;
  firmName?: string | null;
  teamCount?: number;
  team?: { initials: string; color?: string | null }[];
  expiresAt?: string | null;
}

export const EXPIRED_INVITE_TEXT =
  "This invitation has expired. Ask the person who invited you to send a new one.";

export const INVALID_INVITE_TEXT =
  "This invitation link isn't valid or has expired. Ask your firm to send a new one.";

export const ACCEPT_FAILED_TEXT =
  "Your account couldn't be created. Please try again, or ask your firm to send a new invitation.";

/** The invitation from the GET answer, or null when it can't be used (no email = not a real invitation). */
export const readInvite = (data: unknown): PublicInvite | null => {
  if (!data || typeof data !== "object") return null;
  const invite = data as PublicInvite;
  if (typeof invite.email !== "string" || !invite.email.trim()) return null;
  return invite;
};

/**
 * The sentence to show when accepting failed: the server's own short sentence (plain text, or JSON
 * `{message}` / a JSON string), else the fallback. HTML pages, JSON dumps and stack traces are never shown.
 */
export const acceptFailureText = (body: string | null | undefined): string => {
  let text = (body ?? "").trim();
  if (/^"[\s\S]*"$/.test(text)) {
    try {
      text = String(JSON.parse(text)).trim();
    } catch {
      // keep as is
    }
  } else if (text.startsWith("{")) {
    try {
      const parsed = JSON.parse(text) as { message?: unknown };
      text = typeof parsed.message === "string" ? parsed.message.trim() : "";
    } catch {
      text = "";
    }
  }
  if (
    !text ||
    text.length > 300 ||
    /^[<{[]/.test(text) ||
    text.includes("Exception") ||
    /\n\s+at /.test(text)
  ) {
    return ACCEPT_FAILED_TEXT;
  }
  return text;
};
